import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'
import { User, UserSchema } from '@skop-app/lib-domain/types'
import { fetchUserAttributes } from 'aws-amplify/auth'

import { cognitoMonitoring } from '@/services/auth/cognitoMonitoring.client'

import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { getAmplifyClient } from '@/services/amplifyClient'

import type { UserSessionByUserQuery, UserType } from './user.type'
import {
  getLightUserQuery,
  getUserQuery,
  userByEmailQuery,
  userSessionByUserQuery,
} from './user.graphql'
import { userKeys } from './user.keys'
import { CURRENT_USER_STALE_TIME } from './user.cache'
import { seatAwareUserAdapter } from '@/services/adapters/seatAwareUser'
import { fetchSupportWorkspace } from '@/services/support/support.client'
import { adaptSupportWorkspaceProjection } from '@/services/support/supportContract'

const client = getAmplifyClient()

/**
 * Get the current user
 * @returns The current user with adapted data
 */
export const useUser = <TData = User | null>(
  select?: (data: User) => TData,
) => {
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  const effectiveUserId = impersonatedUserId ?? currentUserId

  return useQuery({
    queryKey: userKeys.currentScoped({
      sessionScopeKey,
      currentUserId,
      impersonatedUserId,
    }),
    queryFn: ({ signal }) =>
      getCurrentUserData({
        currentUserId,
        impersonatedUserId,
        sessionScopeKey,
        signal,
      }),
    retry: false,
    enabled: Boolean(effectiveUserId),
    staleTime: CURRENT_USER_STALE_TIME,
    ...(select ? { select } : {}),
  })
}

type GetCurrentUserDataParams = {
  currentUserId?: string
  impersonatedUserId?: string
  sessionScopeKey?: string
  signal?: AbortSignal
}

const getCurrentUserData = async ({
  currentUserId,
  impersonatedUserId,
  sessionScopeKey = '',
  signal,
}: GetCurrentUserDataParams) => {
  if (currentUserId && impersonatedUserId) {
    return adaptSupportWorkspaceProjection(
      await fetchSupportWorkspace(
        {
          actorUserID: currentUserId,
          targetUserID: impersonatedUserId,
          sessionScopeKey,
        },
        undefined,
        signal,
      ),
    )
  }
  let id = impersonatedUserId ?? currentUserId
  if (!id) {
    const currentUser = await cognitoMonitoring.observe(
      'profile',
      'manual',
      () => fetchUserAttributes(),
    )
    id = (currentUser['custom:userID'] ?? currentUser.sub) as string | undefined
  }

  if (!id) {
    throw new Error('User not found')
  }

  const result = (await client.graphql({
    query: getUserQuery,
    variables: { id },
  })) as GraphQLResult<{ getUser: UserSchema }>

  if (!result.data?.getUser) {
    throw new Error('User not found')
  }

  return seatAwareUserAdapter(result.data.getUser)
}

/**
 * Find a user by email for invite autocompletion
 * @param email - The email of the user to find
 * @returns The user if found, otherwise undefined
 */
export const findUserByEmail = async (
  email: string,
): Promise<{ id: string; name: string } | undefined> => {
  const { data } = (await client.graphql({
    query: userByEmailQuery,
    variables: { email, filter: { source: { ne: 'itmu' } } },
  })) as GraphQLResult<{ userByEmail: UserType }>
  const item = data?.userByEmail.items?.[0]
  return item ? { id: item.id, name: item.name } : undefined
}

/**
 * Get a user session by user ID for security purposes
 * @param userID - The ID of the user to get the session for
 * @returns The user session if found, otherwise undefined
 */
export const useUserSessionByUser = (userID: string | undefined) =>
  useQuery({
    queryKey: userKeys.session(userID),
    queryFn: () => getUserSessionByUser(userID as string),
    enabled: !!userID,
  })

export const useLightUserById = (id: string | undefined) =>
  useQuery({
    queryKey: userKeys.light(id),
    queryFn: () => getLightUserById(id as string),
    enabled: Boolean(id),
  })

const getUserSessionByUser = async (userID: string) => {
  const { data: { userSessionByUser } = {} } = (await client.graphql({
    query: userSessionByUserQuery,
    variables: { userID },
  })) as { data?: UserSessionByUserQuery }
  return userSessionByUser?.items
}

// TODO: Implement a real batch fetching function for users
// This is a placeholder and will still result in N+1 calls if used as is.
// The actual implementation should make a single GraphQL query (or other backend call)
// to fetch multiple users by their IDs.
export const getLightUsersByIds = async (
  userIds: string[],
): Promise<UserSchema[]> => {
  if (userIds.length === 0) return []
  // This simulation still makes N calls. Replace with a true batch call.
  const users = await Promise.all(userIds.map((id) => getLightUserById(id)))
  return users.filter(
    (user) => user !== null && user !== undefined,
  ) as UserSchema[]
}

const getLightUserById = async (id: string) => {
  const result = (await client.graphql({
    query: getLightUserQuery,
    variables: { id },
  })) as GraphQLResult<{ getUser: UserSchema }>

  return result.data?.getUser
}

export const selectUser = (data: User | undefined) => {
  return data?.user
}
